import { useEffect, useId, useRef, useState } from 'react';
import cn from 'classnames';
import { Check, Info } from 'lucide-react';
import { priceFormater } from '@/settings';
import i18n from '@/settings/i18n';
import type { OrderDiscountStateT } from '@/lib/order-discounts/types';
import { getDisplayedTiers } from '@/lib/order-discounts/helpers';

const t = i18n.init('shared');

export const DISCOUNT_GREEN = 'text-[#0ca764]';

export const formatDiscountAmount = (minorUnits: number): string =>
  priceFormater.short(minorUnits / 100, minorUnits % 100 !== 0);

const TierRow = ({
  percentage,
  threshold,
  applied,
}: {
  percentage: number;
  threshold: number;
  applied: boolean;
}) => (
  <li className="flex items-center justify-between gap-4">
    <span translate="no">
      <span className={cn('font-bold', DISCOUNT_GREEN)}>-{percentage}%</span>{' '}
      <span className="text-gray-400">
        {t`over ${{ amount: formatDiscountAmount(threshold) }}`}
      </span>
    </span>
    {applied && (
      <span
        className={cn(
          'flex shrink-0 items-center gap-1 font-medium',
          DISCOUNT_GREEN,
        )}
      >
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
        {t`applied`}
      </span>
    )}
  </li>
);

const OrderDiscountBadge = ({
  state,
  className,
}: {
  state: OrderDiscountStateT;
  className?: string;
}) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  const open = pinned || hovered;

  useEffect(() => {
    if (!pinned) {
      return undefined;
    }

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setPinned(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPinned(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pinned]);

  const { visible, tiers, activeTier, nextTier, remaining } = state;

  if (!visible || tiers.length === 0) {
    return <div className={className} />;
  }

  const label = nextTier
    ? t`${{ amount: formatDiscountAmount(remaining) }} away from -${{
        percent: nextTier.percentage,
      }}% off`
    : t`-${{ percent: activeTier?.percentage ?? 0 }}% off unlocked`;

  return (
    <div
      className={cn('relative', className)}
      ref={containerRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        className={cn(
          'flex items-center gap-1 rounded-full bg-green-set-200 px-2.5 py-1 text-xs font-bold',
          DISCOUNT_GREEN,
        )}
        aria-expanded={open}
        aria-controls={tooltipId}
        onClick={() => setPinned((wasPinned) => !wasPinned)}
      >
        <span translate="no">{label}</span>
        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="sr-only">{t`Order discounts`}</span>
      </button>
      {open && (
        <div
          id={tooltipId}
          role="tooltip"
          className="absolute right-0 top-full z-30 mt-2 w-60 rounded-xl border bg-white p-4 text-xs text-gray-700 shadow-[0_8px_24px_0_rgba(0,0,0,0.08)]"
        >
          <p className="font-bold">{t`Order discounts`}</p>
          <ul className="mt-2 space-y-1.5">
            {getDisplayedTiers({ tiers, activeTier }).map((tier) => (
              <TierRow
                key={tier.threshold}
                percentage={tier.percentage}
                threshold={tier.threshold}
                applied={tier.applied}
              />
            ))}
          </ul>
          <p className="mt-3 text-gray-400">{t`Discount applied automatically to your device subtotal.`}</p>
        </div>
      )}
    </div>
  );
};

export default OrderDiscountBadge;
